/**
 * Condition Document Template Service
 *
 * Service for managing condition document templates - PDF templates mapped
 * to Encompass condition External IDs for automatic merging with loan data.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import {
  ConditionTemplate,
  ConditionTemplateRow,
  ConditionTemplateInfo,
  CreateConditionTemplateRequest,
  UpdateConditionTemplateRequest,
  rowToConditionTemplate
} from '@/types/condition-templates';

const log = createComponentLogger('ConditionTemplateService', 'condition-templates');

/**
 * Cache configuration
 */
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * In-memory cache for template lookups
 */
class TemplateCache {
  private cache = new Map<string, { data: ConditionTemplate; expiresAt: number }>();
  private allTemplatesCache: { data: ConditionTemplate[]; expiresAt: number } | null = null;

  get(externalId: string): ConditionTemplate | null {
    const entry = this.cache.get(externalId);
    if (entry && entry.expiresAt > Date.now()) {
      return entry.data;
    }
    if (entry) {
      this.cache.delete(externalId);
    }
    return null;
  }

  set(externalId: string, template: ConditionTemplate): void {
    this.cache.set(externalId, {
      data: template,
      expiresAt: Date.now() + CACHE_TTL
    });
  }

  getAll(): ConditionTemplate[] | null {
    if (this.allTemplatesCache && this.allTemplatesCache.expiresAt > Date.now()) {
      return this.allTemplatesCache.data;
    }
    this.allTemplatesCache = null;
    return null;
  }

  setAll(templates: ConditionTemplate[]): void {
    this.allTemplatesCache = {
      data: templates,
      expiresAt: Date.now() + CACHE_TTL
    };
    // Also populate individual cache entries
    templates.forEach((t) => this.set(t.externalId, t));
  }

  invalidate(externalId?: string): void {
    if (externalId) {
      this.cache.delete(externalId);
    } else {
      this.cache.clear();
    }
    this.allTemplatesCache = null;
  }
}

class ConditionTemplateService {
  private static instance: ConditionTemplateService;
  private cache = new TemplateCache();

  private constructor() {}

  static getInstance(): ConditionTemplateService {
    if (!ConditionTemplateService.instance) {
      ConditionTemplateService.instance = new ConditionTemplateService();
    }
    return ConditionTemplateService.instance;
  }

  /**
   * Get template by External ID
   */
  async getTemplateByExternalId(externalId: string): Promise<ConditionTemplate | null> {
    // Check cache first
    const cached = this.cache.get(externalId);
    if (cached) {
      log.debug('Cache hit for template', { externalId });
      return cached;
    }

    log.info('Fetching template by external ID', { externalId });

    const { data, error } = await supabase
      .from('condition_document_templates')
      .select('*')
      .eq('external_id', externalId)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      log.error('Failed to fetch template', { externalId }, error);
      throw new Error(`Failed to fetch template: ${error.message}`);
    }

    const template = rowToConditionTemplate(data as ConditionTemplateRow);
    this.cache.set(externalId, template);
    return template;
  }

  /**
   * Get all active templates
   */
  async getActiveTemplates(): Promise<ConditionTemplate[]> {
    // Check cache first
    const cached = this.cache.getAll();
    if (cached) {
      log.debug('Cache hit for all templates');
      return cached;
    }

    log.info('Fetching all active templates');

    const { data, error } = await supabase
      .from('condition_document_templates')
      .select('*')
      .eq('is_active', true)
      .order('external_id', { ascending: true });

    if (error) {
      log.error('Failed to fetch templates', {}, error);
      throw new Error(`Failed to fetch templates: ${error.message}`);
    }

    const templates = (data as ConditionTemplateRow[]).map(rowToConditionTemplate);
    this.cache.setAll(templates);
    return templates;
  }

  /**
   * Get template info map for quick lookups
   * Returns Map<externalId, templateInfo>
   */
  async getTemplateInfoMap(): Promise<Map<string, ConditionTemplateInfo>> {
    const templates = await this.getActiveTemplates();
    const map = new Map<string, ConditionTemplateInfo>();

    templates.forEach((t) => {
      map.set(t.externalId, {
        externalId: t.externalId,
        templateName: t.templateName,
        conditionTitle: t.conditionTitle
      });
    });

    return map;
  }

  /**
   * Upload a new template
   */
  async uploadTemplate(
    file: File,
    metadata: CreateConditionTemplateRequest
  ): Promise<ConditionTemplate> {
    log.info('Uploading new template', { externalId: metadata.externalId });

    // Generate storage path
    const timestamp = Date.now();
    const sanitizedName = metadata.templateName.replace(/[^a-zA-Z0-9]/g, '_');
    const storagePath = `corporate/${metadata.externalId}_${sanitizedName}_v1_${timestamp}.pdf`;

    // Upload file to storage
    const { error: uploadError } = await supabase.storage
      .from('condition-templates')
      .upload(storagePath, file, {
        contentType: 'application/pdf',
        upsert: false
      });

    if (uploadError) {
      log.error('Failed to upload template file', { storagePath }, uploadError);
      throw new Error(`Failed to upload template: ${uploadError.message}`);
    }

    // Create database record
    const { data, error } = await supabase
      .from('condition_document_templates')
      .insert({
        external_id: metadata.externalId,
        condition_title: metadata.conditionTitle,
        condition_category: metadata.conditionCategory || null,
        prior_to: metadata.priorTo || null,
        template_name: metadata.templateName,
        template_description: metadata.templateDescription || null,
        storage_path: storagePath,
        storage_bucket: 'condition-templates',
        original_filename: file.name,
        file_size: file.size,
        mime_type: 'application/pdf',
        placeholders: metadata.placeholders || [],
        version: 1,
        is_active: true,
        is_locked: false
      })
      .select()
      .single();

    if (error) {
      // Cleanup uploaded file
      await supabase.storage.from('condition-templates').remove([storagePath]);
      log.error('Failed to create template record', { externalId: metadata.externalId }, error);
      throw new Error(`Failed to create template: ${error.message}`);
    }

    this.cache.invalidate();
    const template = rowToConditionTemplate(data as ConditionTemplateRow);
    log.info('Template uploaded successfully', { id: template.id, externalId: template.externalId });
    return template;
  }

  /**
   * Update template metadata
   */
  async updateTemplate(
    id: string,
    updates: UpdateConditionTemplateRequest
  ): Promise<ConditionTemplate> {
    log.info('Updating template', { id });

    const updateData: Record<string, unknown> = {};
    if (updates.conditionTitle !== undefined) {
updateData.condition_title = updates.conditionTitle;
}
    if (updates.conditionCategory !== undefined) {
updateData.condition_category = updates.conditionCategory;
}
    if (updates.priorTo !== undefined) {
updateData.prior_to = updates.priorTo;
}
    if (updates.templateName !== undefined) {
updateData.template_name = updates.templateName;
}
    if (updates.templateDescription !== undefined) {
updateData.template_description = updates.templateDescription;
}
    if (updates.placeholders !== undefined) {
updateData.placeholders = updates.placeholders;
}
    if (updates.isActive !== undefined) {
updateData.is_active = updates.isActive;
}
    if (updates.isLocked !== undefined) {
updateData.is_locked = updates.isLocked;
}

    const { data, error } = await supabase
      .from('condition_document_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      log.error('Failed to update template', { id }, error);
      throw new Error(`Failed to update template: ${error.message}`);
    }

    this.cache.invalidate();
    const template = rowToConditionTemplate(data as ConditionTemplateRow);
    log.info('Template updated successfully', { id: template.id });
    return template;
  }

  /**
   * Archive (soft delete) a template
   */
  async archiveTemplate(id: string): Promise<void> {
    log.info('Archiving template', { id });

    const { error } = await supabase
      .from('condition_document_templates')
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      log.error('Failed to archive template', { id }, error);
      throw new Error(`Failed to archive template: ${error.message}`);
    }

    this.cache.invalidate();
    log.info('Template archived successfully', { id });
  }

  /**
   * Get signed download URL for a template file
   */
  async getTemplateDownloadUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from('condition-templates')
      .createSignedUrl(storagePath, 3600); // 1 hour expiry

    if (error) {
      log.error('Failed to create signed URL', { storagePath }, error);
      throw new Error(`Failed to create download URL: ${error.message}`);
    }

    return data.signedUrl;
  }

  /**
   * Invalidate cache
   */
  invalidateCache(): void {
    this.cache.invalidate();
    log.debug('Cache invalidated');
  }

  /**
   * Generate and download a condition document with merged loan data
   * Calls the generate-condition-document edge function
   * Uses raw fetch instead of supabase.functions.invoke for reliable binary handling
   */
  async downloadConditionDocument(externalId: string, loanGuid: string, borrowerPairId?: string): Promise<Blob> {
    log.info('Generating condition document', { externalId, loanGuid, borrowerPairId });

    const { data: sessionData } = await supabase.auth.getSession();

    if (!sessionData?.session?.access_token) {
      throw new Error('Not authenticated');
    }

    // Use raw fetch for reliable binary PDF response handling
    const requestBody: Record<string, string> = { externalId, loanGuid };
    if (borrowerPairId) {
      requestBody.borrowerPairId = borrowerPairId;
    }

    const response = await fetch(
      `${(supabase as unknown as { supabaseUrl: string }).supabaseUrl}/functions/v1/generate-condition-document`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session.access_token}`,
          apikey: (supabase as unknown as { supabaseKey: string }).supabaseKey
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      log.error('Failed to generate condition document', { externalId, loanGuid, borrowerPairId, status: response.status, error: errorText });
      throw new Error(`Failed to generate document: ${errorText}`);
    }

    const blob = await response.blob();

    log.info('Condition document generated successfully', {
      externalId,
      size: blob.size,
      type: blob.type
    });

    return blob;
  }

  /**
   * Download and save a condition document to the user's device
   */
  async downloadAndSaveConditionDocument(
    externalId: string,
    loanGuid: string,
    suggestedFilename?: string,
    borrowerPairId?: string
  ): Promise<void> {
    const blob = await this.downloadConditionDocument(externalId, loanGuid, borrowerPairId);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = suggestedFilename || `condition_${externalId}_${loanGuid.substring(0, 8)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    log.info('Condition document downloaded', { externalId, filename: link.download });
  }
}

// Export singleton instance
export const conditionTemplateService = ConditionTemplateService.getInstance();
